// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ChartWrapper, spacing, typography } from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import { FC } from "react";
import useMeasure from "react-use-measure";
import OrdinalFrame from "semiotic/lib/OrdinalFrame";
import styled from "styled-components";

import { preventFlexibleLayoutOverflow } from "~@jii/common-ui";
import { usMaEarnedCreditTypes } from "~@jii/schemas";
import { useUsMaTranslations } from "~@jii/translation";
import { palette } from "~design-system";

import { ChartDatum, ChartPresenterInterface } from "./types";

// these are intentionally different from what's in the design system.
// kept private to this module pending further direction from the design team
// regarding their broader use beyond this chart
const CHART_COLORS = {
  [usMaEarnedCreditTypes.enum.earnedGoodTime]: "#78BA43",
  [usMaEarnedCreditTypes.enum.boost]: "#00A49A",
  [usMaEarnedCreditTypes.enum.completion]: "#123C66",
};

const Wrapper = styled.div`
  position: relative;
`;

const NoDataMessage = styled.div`
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  color: ${palette.slate60};
`;

const Legend = styled.div`
  ${typography.Sans14}

  display: flex;
  justify-content: center;
  gap: ${rem(spacing.lg)};
`;

const LegendItem = styled.div<{ swatch: string }>`
  &::before {
    background: ${(props) => props.swatch};
    border-radius: 1em;
    content: "";
    display: inline-block;
    height: 0.8em;
    margin-right: 0.5em;
    vertical-align: baseline;
    width: 0.8em;
  }

  ${preventFlexibleLayoutOverflow}
`;

const CHART_MARGINS = { left: 60, bottom: 40, top: 20 };

const ChartComponent: FC<{ presenter: ChartPresenterInterface }> = observer(
  function Chart({ presenter }) {
    const [measureRef, { width }] = useMeasure();
    const { t } = useUsMaTranslations();

    // Use the max value from axisTicks to set the range extent
    const maxTickValue = presenter.axisTicks
      ? Math.max(...presenter.axisTicks)
      : undefined;

    // Only show no data message for skeleton charts
    const isSkeleton = presenter.isSkeleton === true;

    return (
      <Wrapper ref={measureRef}>
        <ChartWrapper>
          <OrdinalFrame
            size={[width, 300]}
            type="bar"
            data={presenter.chartData}
            oAccessor={(d) =>
              // t freaks out here if the second argument isn't an object type
              t(($) => $.home.creditHistory.monthLabel, d as ChartDatum)
            }
            rAccessor="totalCredits"
            rExtent={maxTickValue !== undefined ? [0, maxTickValue] : undefined}
            style={(d: ChartDatum) => ({
              fill: CHART_COLORS[d.creditType],
            })}
            oPadding={20}
            oLabel
            axes={[
              {
                orient: "left",
                // this is not really a component (it doesn't accept props)
                // eslint-disable-next-line react/no-unstable-nested-components
                tickFormat: (count: number) => (
                  <text
                    // 10 is the offset automatically applied by Semiotic,
                    // which we have to take into account when positioning at the left edge.
                    // plus a couple extra pixels to guard against clipping
                    dx={-CHART_MARGINS.left + 10 + 2}
                    textAnchor="start"
                    dy={-8}
                  >
                    {t(($) => $.home.creditHistory.creditLabel, { count })}
                  </text>
                ),
                tickValues: presenter.axisTicks,
                baseline: false,
                // @ts-expect-error its ok to return an SVG element here
                // eslint-disable-next-line react/no-unstable-nested-components
                tickLineGenerator: ({ xy }) => {
                  return (
                    <line
                      {...xy}
                      // @ts-expect-error semiotic types are inaccurate here
                      x1={xy.x1 - CHART_MARGINS.left}
                      className="tick-line"
                    />
                  );
                },
              },
            ]}
            margin={CHART_MARGINS}
          />
          {isSkeleton && (
            <NoDataMessage>
              {t(($) => $.home.creditHistory.noDataMessage)}
            </NoDataMessage>
          )}
        </ChartWrapper>
        <Legend>
          <LegendItem swatch={CHART_COLORS.earnedGoodTime}>
            {t(($) => $.home.creditHistory.legend.earnedGoodTime)}
          </LegendItem>
          <LegendItem swatch={CHART_COLORS.boost}>
            {t(($) => $.home.creditHistory.legend.boost)}
          </LegendItem>
          <LegendItem swatch={CHART_COLORS.completion}>
            {t(($) => $.home.creditHistory.legend.completion)}
          </LegendItem>
        </Legend>
      </Wrapper>
    );
  },
);

export { ChartComponent };
